import { useEffect, useState, useMemo } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Save, RotateCcw, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { code: "executive",    label: "Executive Management" },
  { code: "ops_mgmt",     label: "Operations Management" },
  { code: "bu_manager",   label: "BU Manager" },
  { code: "bu_support",   label: "BU Support" },
  { code: "admin",        label: "General Admin" },
  { code: "finance_admin", label: "Financial Administration" },
  { code: "field_worker", label: "Field Worker" },
  { code: "client",       label: "Client" },
];

const ACTIONS = ["view", "create", "edit", "delete", "approve", "publish", "export", "admin"];

export default function PermissionGroupsPage() {
  const [role, setRole] = useState("bu_manager");
  const [effective, setEffective] = useState(null);
  const [override, setOverride] = useState(null); // {module: [actions]}
  const [description, setDescription] = useState("");
  const [overridesList, setOverridesList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);

  const loadList = async () => {
    const r = await api.get("/permission-groups");
    setOverridesList(r.data.items || []);
  };

  const loadModules = async () => {
    try {
      const r = await api.get("/org/rbac/catalogue");
      setModules(r.data.modules || []);
    } catch (e) { console.warn("[PermissionGroups] loadModules failed", e?.message || e); }
  };

  const loadRole = async (rc) => {
    setLoading(true);
    try {
      const r = await api.get(`/permission-groups/effective/${rc}`);
      setEffective(r.data);
      setOverride(r.data.override ? { ...r.data.override } : { ...r.data.baseline });
      const doc = overridesList.find((x) => x.role_code === rc);
      setDescription(doc?.description || `Custom permissions for '${rc}'`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load role");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadModules(); loadList(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadRole(role); /* eslint-disable-next-line */ }, [role, overridesList.length]);

  const toggleAction = (mod, act) => {
    setOverride((cur) => {
      const next = { ...cur };
      const list = next[mod] ? [...next[mod]] : [];
      const idx = list.indexOf(act);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(act);
      next[mod] = list;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/permission-groups/${role}`,
        { matrix: override, description, active: true });
      toast.success(`Override saved for '${role}'`);
      await loadList();
      await loadRole(role);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetToBaseline = async () => {
    if (!window.confirm(`Delete the override for '${role}' and revert to the baseline matrix?`)) return;
    setSaving(true);
    try {
      await api.delete(`/permission-groups/${role}`);
      toast.success("Reset to baseline");
      await loadList();
      await loadRole(role);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const differsFromBaseline = useMemo(() => {
    if (!effective?.baseline || !override) return false;
    const keys = new Set([...Object.keys(effective.baseline), ...Object.keys(override)]);
    for (const m of keys) {
      const a = new Set(effective.baseline[m] || []);
      const b = new Set(override[m] || []);
      if (a.size !== b.size) return true;
      for (const x of a) if (!b.has(x)) return true;
    }
    return false;
  }, [effective, override]);

  return (
    <AppLayout
      title="Permission Groups"
      subtitle="Admin-editable overrides on top of the baseline RBAC matrix"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-[220px] h-9 bg-white" data-testid="pg-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => { loadList(); loadRole(role); }} disabled={loading} data-testid="pg-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      }
    >
      {effective && (
        <div className="mb-4 rounded-md border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3 text-sm">
          <ShieldCheck className="w-4 h-4 text-slate-500" />
          <span className="text-slate-700">Role: <strong>{role}</strong></span>
          {effective.override_active
            ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Custom override active</Badge>
            : <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Using baseline</Badge>}
          {differsFromBaseline && (
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Unsaved changes</Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={resetToBaseline} disabled={saving || !effective.override_active}
              className="text-rose-700 hover:text-rose-800" data-testid="pg-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to baseline
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="pg-save-btn">
              <Save className={`w-3.5 h-3.5 mr-1.5 ${saving ? "animate-pulse" : ""}`} /> Save override
            </Button>
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Description</label>
        <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Note explaining this custom override…" data-testid="pg-desc" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left font-medium px-3 py-2 sticky left-0 bg-slate-50 z-10">Module</th>
                {ACTIONS.map((a) => (
                  <th key={a} className="text-center font-medium px-2 py-2 min-w-[70px]">{a}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => {
                const baseActs = new Set(effective?.baseline?.[m.code] || []);
                const overActs = new Set(override?.[m.code] || []);
                return (
                  <tr key={m.code} data-testid={`pg-row-${m.code}`} className="border-b border-slate-100 hover:bg-slate-50/40">
                    <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 bg-white">
                      {m.name}
                      <div className="text-[10px] text-slate-400">{m.code}</div>
                    </td>
                    {ACTIONS.map((a) => {
                      const inBase = baseActs.has(a);
                      const inOverride = overActs.has(a);
                      const changed = inBase !== inOverride;
                      return (
                        <td key={a} className="text-center px-2 py-1.5">
                          <label className="inline-flex items-center justify-center cursor-pointer">
                            <input type="checkbox" checked={inOverride}
                              onChange={() => toggleAction(m.code, a)}
                              data-testid={`pg-toggle-${m.code}-${a}`}
                              className={`w-4 h-4 rounded ${changed ? "ring-2 ring-amber-400" : ""}`} />
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 flex items-start gap-1.5">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Overrides take effect immediately on save — the in-memory cache is refreshed automatically.
          Cells with an amber ring differ from the baseline. Reset to baseline removes the custom override entirely.
        </span>
      </div>
    </AppLayout>
  );
}
